import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiStar, FiShoppingCart, FiHeart, FiPlus, FiMinus, FiShield,
  FiPackage, FiArrowLeft, FiCheckCircle, FiChevronRight, FiChevronLeft,
  FiX, FiChevronDown, FiChevronUp, FiInfo, FiUsers, FiAward, FiClock,
  FiHome, FiMapPin, FiRefreshCw, FiTag, FiGrid, FiCoffee, FiThermometer,
  FiGlobe, FiTarget, FiZap, FiDroplet, FiWind, FiSun, FiMail, FiPhone,
  FiCalendar, FiScissors, FiLayers, FiMessageSquare, FiThumbsUp, FiUser,
  FiLock, FiUpload, FiImage
} from "react-icons/fi";
import { Leaf, Package, Scale, Clock, Droplets, Thermometer } from 'lucide-react';
import { useCart } from "../App";
import { vendorService } from "../services/vendorService";
import ProductBadges from "./ProductBadges";
import ShareButton from "./ShareButton";
import havbitLogo from '../assests/logo/havbit 6.png';

const API_BASE_URL          = "https://server.havbit.in/api";
const PUBLIC_PRODUCTS_API  = `${API_BASE_URL}/public/products`;
const PUBLIC_BRANDS_API    = `${API_BASE_URL}/public/brands`;
const REVIEWS_API          = `${API_BASE_URL}/reviews`;
const APPROVED_REVIEWS_API = `${API_BASE_URL}/reviews/approved`;
const RECENTLY_VIEWED_KEY = "recently_viewed_products";

/* ─── helpers ─── */
const createSlug = (name) => {
  if (!name) return '';
  return name.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/(^-|-$)/g, '');
};
const cleanApiSlug   = (slug) => slug ? slug.replace(/-\d{10,}$/, '') : '';
const getProductSlug = (product) =>
  product.slug ? cleanApiSlug(product.slug) : createSlug(product.name);

const hasUserPurchasedProduct = (productId) => {
  try {
    const orders = JSON.parse(localStorage.getItem('confirmed_orders') || '[]');
    return orders.some(order =>
      Array.isArray(order.productIds) && order.productIds.includes(String(productId))
    );
  } catch { return false; }
};

/* ─── PhotoUploader ─── */
const PhotoUploader = ({ files, setFiles }) => {
  const inputRef = useRef(null);
  const MAX = 5;
  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid   = selected.filter(f => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024);
    const invalid = selected.filter(f => !f.type.startsWith('image/') || f.size > 5 * 1024 * 1024);
    if (invalid.length > 0) alert(`${invalid.length} file(s) skipped: only images under 5MB are allowed.`);
    setFiles(prev => [...prev, ...valid].slice(0, MAX));
    e.target.value = '';
  };
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));
  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={files.length >= MAX}
        className={`w-full border-2 border-dashed rounded-xl p-4 flex flex-col items-center gap-1.5 transition-all duration-200 ${files.length >= MAX ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' : 'border-gray-300 hover:border-red-400 hover:bg-red-50 cursor-pointer'}`}
      >
        <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
          <FiUpload size={16} className="text-blue-500" />
        </div>
        <p className="text-sm font-semibold text-gray-700">
          {files.length >= MAX ? 'Maximum 5 photos added' : 'Tap to upload photos'}
        </p>
        <p className="text-xs text-gray-400">JPG, PNG · max 5MB each · up to 5 photos</p>
      </button>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {files.map((file, idx) => (
            <div key={idx} className="relative w-16 h-16 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0 shadow-sm">
              <img src={URL.createObjectURL(file)} alt={`preview-${idx}`} className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeFile(idx)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors">
                <FiX size={10} />
              </button>
            </div>
          ))}
          {files.length < MAX && (
            <button type="button" onClick={() => inputRef.current?.click()} className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-red-400 hover:text-red-400 hover:bg-red-50 transition-all flex-shrink-0">
              <FiPlus size={18} /><span className="text-[9px] mt-0.5">Add</span>
            </button>
          )}
        </div>
      )}
      {files.length > 0 && <p className="text-[10px] text-gray-400 mt-1.5">{files.length}/{MAX} photos selected</p>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MOBILE review card
══════════════════════════════════════════════════════════════ */
const ReviewCardMobile = ({ review, onPhotoClick }) => {
  const initials = (review.userName || 'A').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const formattedDate = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const hasPhotos = review.gallery?.length > 0;
  const badgeBg = review.rating >= 4 ? 'bg-green-600' : review.rating === 3 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="flex-shrink-0 w-56 bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className={`flex items-center gap-1 ${badgeBg} px-2 py-0.5 rounded`}>
          <span className="text-xs font-bold text-white">{review.rating}</span>
          <FiStar size={9} className="text-white fill-current" />
        </div>
        {formattedDate && <span className="text-[10px] text-gray-400">{formattedDate}</span>}
      </div>
      {review.comment && <p className="text-[11px] text-gray-600 mb-2 line-clamp-3 leading-relaxed">{review.comment}</p>}
      {hasPhotos && (
        <div className="flex gap-1.5 mb-2">
          {review.gallery.slice(0, 3).map((url, idx) => (
            <button key={idx} onClick={() => onPhotoClick(url)} className="w-11 h-11 rounded-lg overflow-hidden border border-gray-100 flex-shrink-0">
              <img src={url} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display = 'none'; }} />
            </button>
          ))}
          {review.gallery.length > 3 && (
            <div className="w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
              +{review.gallery.length - 3}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-auto pt-1 border-t border-gray-50">
        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0">{initials}</div>
        <span className="text-[10px] font-semibold text-gray-700 truncate flex-1">{review.userName || 'Anonymous'}</span>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <FiCheckCircle size={9} className="text-green-500" />
          <span className="text-[9px] text-green-600">Verified</span>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   DESKTOP review card — compact Flipkart style
══════════════════════════════════════════════════════════════ */
const ReviewCardDesktop = ({ review, onPhotoClick }) => {
  const initials = (review.userName || 'A').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const formattedDate = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const hasPhotos = review.gallery?.length > 0;
  const badgeBg = review.rating >= 4 ? 'bg-green-600' : review.rating === 3 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="py-4 px-5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-start gap-3">
        {/* Rating badge */}
        <div className={`flex items-center gap-0.5 ${badgeBg} px-2 py-0.5 rounded flex-shrink-0 mt-0.5`}>
          <span className="text-xs font-bold text-white">{review.rating}</span>
          <FiStar size={9} className="text-white fill-current" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Comment */}
          {review.comment && (
            <p className="text-sm text-gray-800 leading-snug mb-2">{review.comment}</p>
          )}
          {/* Photos */}
          {hasPhotos && (
            <div className="flex gap-2 mb-2 flex-wrap">
              {review.gallery.map((url, idx) => (
                <button key={idx} onClick={() => onPhotoClick(url)}
                  className="w-14 h-14 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-sm transition-all flex-shrink-0">
                  <img src={url} alt="" className="w-full h-full object-cover"
                    onError={e => { e.target.parentElement.style.display = 'none'; }} />
                </button>
              ))}
            </div>
          )}
          {/* Meta row */}
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0">{initials}</div>
            <span className="text-xs font-semibold text-gray-700">{review.userName || 'Anonymous'}</span>
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-0.5">
              <FiCheckCircle size={10} className="text-green-500" />
              <span className="text-[10px] text-green-600 font-medium">Certified Buyer</span>
            </div>
            {formattedDate && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-[10px] text-gray-400">{formattedDate}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   REVIEWS SECTION — COMPACT FLIPKART DESKTOP UI
══════════════════════════════════════════════════════════════ */
const ReviewsSection = ({ productId, onStatsChange }) => {
  const [reviews, setReviews]               = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError]     = useState(null);
  const [submitting, setSubmitting]         = useState(false);
  const [submitSuccess, setSubmitSuccess]   = useState(false);
  const [submitError, setSubmitError]       = useState(null);
  const [showForm, setShowForm]             = useState(false);
  const [hasPurchased, setHasPurchased]     = useState(false);
  const [photoFiles, setPhotoFiles]         = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [lightboxImg, setLightboxImg]       = useState(null);
  const [formData, setFormData]             = useState({ userName: '', userEmail: '', rating: 0, comment: '' });
  const [fieldErrors, setFieldErrors]       = useState({});

  useEffect(() => { setHasPurchased(hasUserPurchasedProduct(productId)); }, [productId]);

  const fetchApprovedReviews = async () => {
    try {
      setReviewsLoading(true); setReviewsError(null);
      const res  = await fetch(APPROVED_REVIEWS_API);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const all  = data?.data || data?.reviews || [];
      setReviews(all);
      if (all.length > 0) {
        const avg = parseFloat((all.reduce((s, r) => s + (r.rating || 0), 0) / all.length).toFixed(1));
        onStatsChange?.({ rating: avg, reviewCount: all.length });
      }
    } catch { setReviewsError('Could not load reviews.'); }
    finally { setReviewsLoading(false); }
  };

  useEffect(() => { fetchApprovedReviews(); }, []);

  const validate = () => {
    const errors = {};
    if (!formData.userName.trim())  errors.userName  = 'Name is required';
    if (!formData.userEmail.trim()) errors.userEmail = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) errors.userEmail = 'Enter a valid email';
    if (!formData.rating || formData.rating < 1) errors.rating = 'Please select a rating';
    if (!formData.comment.trim())   errors.comment = 'Please write your review';
    return errors;
  };

  const handleSubmitReview = async (e) => {
    e?.preventDefault(); setSubmitError(null);
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    try {
      setSubmitting(true);
      const payload = new FormData();
      payload.append('userName',  formData.userName.trim());
      payload.append('userEmail', formData.userEmail.trim().toLowerCase());
      payload.append('rating',    String(formData.rating));
      payload.append('comment',   formData.comment.trim());
      payload.append('source',    'website');
      photoFiles.forEach(file => payload.append('gallery', file));
      const res    = await fetch(REVIEWS_API, { method: 'POST', body: payload });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.message || 'Failed to submit review');
      setSubmitSuccess(true);
      setFormData({ userName: '', userEmail: '', rating: 0, comment: '' });
      setPhotoFiles([]); setShowForm(false);
      setTimeout(() => { fetchApprovedReviews(); setSubmitSuccess(false); }, 3000);
    } catch (err) { setSubmitError(err.message || 'Something went wrong.'); }
    finally { setSubmitting(false); }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: '' }));
  };

  const resetForm = () => { setShowForm(false); setFieldErrors({}); setSubmitError(null); setPhotoFiles([]); };

  /* ── derived stats ── */
  const avgRating = reviews.length
    ? parseFloat((reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)) : 0;
  const ratingLabel = avgRating >= 4.5 ? 'Excellent' : avgRating >= 4 ? 'Very Good' : avgRating >= 3 ? 'Good' : avgRating >= 2 ? 'Average' : 'Poor';
  const ratingCounts = [5, 4, 3, 2, 1].map(star => ({ star, count: reviews.filter(r => r.rating === star).length }));
  const barColor = (star) => star >= 4 ? '#22c55e' : star === 3 ? '#f59e0b' : '#ef4444';
  const allPhotos      = reviews.flatMap(r => r.gallery || []).filter(Boolean);
  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 4);

  /* ── Shared inline form ── */
  const ReviewForm = () => (
    <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-4">
      <h3 className="text-sm font-bold text-gray-900 mb-3">Share Your Experience</h3>
      <form onSubmit={handleSubmitReview}>
        {/* Star rating */}
        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Your Rating <span className="text-red-500">*</span></label>
          <div className="flex items-center gap-1.5">
            {[1,2,3,4,5].map(star => (
              <button key={star} type="button" onClick={() => handleFieldChange('rating', star)}>
                <FiStar size={24} className={`transition-all ${star <= formData.rating ? 'text-amber-400 fill-current' : 'text-gray-300 hover:text-amber-300'}`} />
              </button>
            ))}
            {formData.rating > 0 && (
              <span className="ml-1 text-xs font-bold text-amber-500">{['','Terrible','Bad','Okay','Good','Excellent!'][formData.rating]}</span>
            )}
          </div>
          {fieldErrors.rating && <p className="text-[11px] text-red-500 mt-1">{fieldErrors.rating}</p>}
        </div>
        {/* 2-col name/email */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Name <span className="text-red-500">*</span></label>
            <input type="text" value={formData.userName} onChange={e => handleFieldChange('userName', e.target.value)} placeholder="Your name" maxLength={60}
              className={`w-full border rounded-lg px-3 py-1.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 bg-white ${fieldErrors.userName ? 'border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'}`} />
            {fieldErrors.userName && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrors.userName}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Email <span className="text-red-500">*</span></label>
            <input type="email" value={formData.userEmail} onChange={e => handleFieldChange('userEmail', e.target.value)} placeholder="Your email (private)" maxLength={100}
              className={`w-full border rounded-lg px-3 py-1.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 bg-white ${fieldErrors.userEmail ? 'border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'}`} />
            {fieldErrors.userEmail && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrors.userEmail}</p>}
          </div>
        </div>
        {/* Review text */}
        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Review <span className="text-red-500">*</span></label>
          <textarea value={formData.comment} onChange={e => handleFieldChange('comment', e.target.value)} placeholder="Describe your experience with this product..." rows={3} maxLength={500}
            className={`w-full border rounded-lg px-3 py-1.5 text-sm placeholder-gray-400 resize-none focus:outline-none focus:ring-1 bg-white ${fieldErrors.comment ? 'border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'}`} />
          <div className="flex items-center justify-between">
            {fieldErrors.comment ? <p className="text-[11px] text-red-500">{fieldErrors.comment}</p> : <span />}
            <p className="text-[10px] text-gray-400">{formData.comment.length}/500</p>
          </div>
        </div>
        {/* Photo upload */}
        <div className="mb-3">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1">
            <FiImage size={11} />Add Photos
            <span className="text-[10px] font-normal text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded-full">optional · max 5</span>
          </label>
          <PhotoUploader files={photoFiles} setFiles={setPhotoFiles} />
        </div>
        {submitError && (
          <div className="flex items-center gap-2 p-2.5 mb-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
            <FiX size={11} /> {submitError}
          </div>
        )}
        <div className="flex gap-2">
          <button type="submit" disabled={submitting}
            className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
            {submitting
              ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />{photoFiles.length > 0 ? 'Uploading...' : 'Submitting...'}</>
              : <><FiCheckCircle size={12} />Submit Review</>}
          </button>
          <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-100 transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {/* ══════════ DESKTOP ══════════ */}
      <div className="hidden lg:block">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">

          {/* Section title row */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <h2 className="text-base font-bold text-gray-900">
              Ratings &amp; Reviews
            </h2>
            {hasPurchased ? (
              <button
                onClick={() => { setShowForm(f => !f); setSubmitSuccess(false); setSubmitError(null); setFieldErrors({}); if (showForm) setPhotoFiles([]); }}
                className="flex items-center gap-1.5 px-4 py-1.5 border border-blue-500 text-blue-600 text-xs font-semibold rounded-full hover:bg-blue-50 transition-colors"
              >
                <FiStar size={11} className="text-amber-400 fill-current" />
                {showForm ? 'Cancel' : 'Rate Product'}
              </button>
            ) : (
              <div className="flex items-center gap-1.5 px-4 py-1.5 border border-gray-200 text-gray-400 text-xs font-semibold rounded-full cursor-not-allowed">
                <FiLock size={11} /> Rate after Purchase
              </div>
            )}
          </div>

          {/* Compact summary: score + bars side by side */}
          {reviews.length > 0 && (
            <div className="flex items-center gap-0 px-5 py-4 border-b border-gray-100">
              {/* Big number */}
              <div className="flex flex-col items-center pr-6 border-r border-gray-100 flex-shrink-0 min-w-[90px]">
                <span className="text-5xl font-black text-gray-900 leading-none">{avgRating}</span>
                <div className="flex items-center gap-0.5 mt-1.5">
                  {[1,2,3,4,5].map(s => (
                    <FiStar key={s} size={11} className={s <= Math.round(avgRating) ? 'text-amber-400 fill-current' : 'text-gray-200 fill-current'} />
                  ))}
                </div>
                <span className="mt-1.5 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">{ratingLabel}</span>
                <span className="text-[9px] text-gray-400 mt-1">{reviews.length} ratings</span>
              </div>

              {/* Bar chart */}
              <div className="flex-1 flex flex-col gap-1.5 px-6">
                {ratingCounts.map(({ star, count }) => (
                  <div key={star} className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500 w-1.5 text-right flex-shrink-0">{star}</span>
                    <FiStar size={9} className="text-amber-400 fill-current flex-shrink-0" />
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: reviews.length ? `${(count / reviews.length) * 100}%` : '0%', backgroundColor: barColor(star) }}
                      />
                    </div>
                    <span className="text-[11px] text-gray-400 w-3 text-right flex-shrink-0">{count}</span>
                  </div>
                ))}
              </div>

              {/* Photos strip */}
              {allPhotos.length > 0 && (
                <div className="pl-6 border-l border-gray-100 flex-shrink-0">
                  <p className="text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                    Customer Photos
                  </p>
                  <div className="flex gap-1.5 flex-wrap max-w-[160px]">
                    {allPhotos.slice(0, 4).map((url, idx) => (
                      <button key={idx} onClick={() => setLightboxImg(url)}
                        className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-sm transition-all group flex-shrink-0">
                        <img src={url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          onError={e => { e.target.parentElement.style.display = 'none'; }} />
                        {idx === 3 && allPhotos.length > 4 && (
                          <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                            <span className="text-white font-bold text-xs">+{allPhotos.length - 4}</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No reviews state */}
          {!reviewsLoading && !reviewsError && reviews.length === 0 && !showForm && (
            <div className="flex items-center justify-between px-5 py-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <FiMessageSquare size={18} className="text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">No reviews yet</p>
                  <p className="text-xs text-gray-400">Be the first to review this product</p>
                </div>
              </div>
            </div>
          )}

          {/* Write review form */}
          <AnimatePresence>
            {showForm && hasPurchased && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                <ReviewForm />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success */}
          <AnimatePresence>
            {submitSuccess && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="mx-5 my-2 p-2.5 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-xs text-green-700 font-medium">
                <FiCheckCircle size={14} className="text-green-500" />
                Review submitted! It will appear after approval. Thank you 🙏
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading */}
          {reviewsLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
              <span className="ml-2 text-xs text-gray-500">Loading reviews...</span>
            </div>
          )}
          {reviewsError && !reviewsLoading && (
            <div className="text-center py-5">
              <p className="text-xs text-gray-500 mb-2">{reviewsError}</p>
              <button onClick={fetchApprovedReviews} className="flex items-center gap-1 mx-auto text-xs text-blue-600 font-medium">
                <FiRefreshCw size={10} /> Retry
              </button>
            </div>
          )}

          {/* Review list */}
          {!reviewsLoading && !reviewsError && reviews.length > 0 && (
            <div>
              {visibleReviews.map((review, index) => (
                <ReviewCardDesktop key={review._id || index} review={review} onPhotoClick={setLightboxImg} />
              ))}
              {reviews.length > 4 && (
                <button
                  onClick={() => setShowAllReviews(v => !v)}
                  className="w-full flex items-center justify-center gap-1.5 py-3.5 text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-colors border-t border-gray-100"
                >
                  {showAllReviews ? 'Show Less' : `View All ${reviews.length} Reviews`}
                  <FiChevronRight size={14} className={`transition-transform ${showAllReviews ? 'rotate-90' : ''}`} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ══════════ MOBILE ══════════ */}
      <div className="lg:hidden bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FiStar className="text-amber-400 fill-current" size={14} />
            Ratings &amp; Reviews
            {reviews.length > 0 && <span className="text-[10px] font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{reviews.length}</span>}
          </h2>
          {hasPurchased ? (
            <button onClick={() => { setShowForm(f => !f); setSubmitSuccess(false); setSubmitError(null); setFieldErrors({}); if (showForm) setPhotoFiles([]); }}
              className="flex items-center gap-1 px-3 py-1.5 border border-blue-500 text-blue-600 text-xs font-semibold rounded-full hover:bg-blue-50">
              <FiStar size={10} className="text-amber-400 fill-current" />
              {showForm ? 'Cancel' : 'Rate'}
            </button>
          ) : (
            <div className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-400 text-xs rounded-full cursor-not-allowed">
              <FiLock size={10} /> After Purchase
            </div>
          )}
        </div>

        {/* Summary */}
        {reviews.length > 0 && (
          <div className="flex gap-4 px-4 py-4 border-b border-gray-100">
            <div className="flex flex-col items-center justify-center min-w-[72px]">
              <span className="text-4xl font-black text-gray-900 leading-none">{avgRating}</span>
              <div className="flex items-center gap-0.5 mt-1.5">
                {[1,2,3,4,5].map(s => <FiStar key={s} size={10} className={s <= Math.round(avgRating) ? 'text-amber-400 fill-current' : 'text-gray-200 fill-current'} />)}
              </div>
              <span className="mt-1 text-[9px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">{ratingLabel}</span>
              <span className="text-[9px] text-gray-400 mt-1">{reviews.length} ratings</span>
            </div>
            <div className="flex-1 flex flex-col gap-1.5 justify-center">
              {ratingCounts.map(({ star, count }) => (
                <div key={star} className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500 w-2 text-right">{star}</span>
                  <FiStar size={8} className="text-amber-400 fill-current flex-shrink-0" />
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: reviews.length ? `${(count / reviews.length) * 100}%` : '0%', backgroundColor: barColor(star) }} />
                  </div>
                  <span className="text-[10px] text-gray-400 w-3 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos strip mobile */}
        {allPhotos.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5"><FiImage size={11} />Photos by Buyers ({allPhotos.length})</p>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {allPhotos.slice(0, 6).map((url, idx) => (
                <button key={idx} onClick={() => setLightboxImg(url)} className="relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                  <img src={url} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display = 'none'; }} />
                  {idx === 5 && allPhotos.length > 6 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-bold text-xs">+{allPhotos.length - 6}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Form mobile */}
        <AnimatePresence>
          {showForm && hasPurchased && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Share Your Experience</h3>
                <form onSubmit={handleSubmitReview} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Rating <span className="text-red-500">*</span></label>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(star => (
                        <button key={star} type="button" onClick={() => handleFieldChange('rating', star)}>
                          <FiStar size={26} className={`transition-all ${star <= formData.rating ? 'text-amber-400 fill-current' : 'text-gray-300'}`} />
                        </button>
                      ))}
                      {formData.rating > 0 && <span className="ml-1.5 text-xs font-bold text-amber-500">{['','Terrible','Bad','Okay','Good','Excellent!'][formData.rating]}</span>}
                    </div>
                    {fieldErrors.rating && <p className="text-[11px] text-red-500 mt-1">{fieldErrors.rating}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Name <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.userName} onChange={e => handleFieldChange('userName', e.target.value)} placeholder="Your name" maxLength={60}
                      className={`w-full border rounded-lg px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 bg-white ${fieldErrors.userName ? 'border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'}`} />
                    {fieldErrors.userName && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrors.userName}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Email <span className="text-red-500">*</span></label>
                    <input type="email" value={formData.userEmail} onChange={e => handleFieldChange('userEmail', e.target.value)} placeholder="Your email (private)" maxLength={100}
                      className={`w-full border rounded-lg px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 bg-white ${fieldErrors.userEmail ? 'border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'}`} />
                    {fieldErrors.userEmail && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrors.userEmail}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Review <span className="text-red-500">*</span></label>
                    <textarea value={formData.comment} onChange={e => handleFieldChange('comment', e.target.value)} placeholder="Tell others what you think..." rows={3} maxLength={500}
                      className={`w-full border rounded-lg px-3 py-2 text-sm placeholder-gray-400 resize-none focus:outline-none focus:ring-1 bg-white ${fieldErrors.comment ? 'border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'}`} />
                    <p className="text-[10px] text-gray-400 text-right">{formData.comment.length}/500</p>
                    {fieldErrors.comment && <p className="text-[11px] text-red-500">{fieldErrors.comment}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1.5">
                      <FiImage size={11} />Photos <span className="text-[10px] font-normal text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded-full">optional · max 5</span>
                    </label>
                    <PhotoUploader files={photoFiles} setFiles={setPhotoFiles} />
                  </div>
                  {submitError && <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600"><FiX size={11} />{submitError}</div>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={submitting} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                      {submitting ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{photoFiles.length > 0 ? 'Uploading...' : 'Submitting...'}</> : <><FiCheckCircle size={13} />Submit Review</>}
                    </button>
                    <button type="button" onClick={resetForm} className="px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100">Cancel</button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success toast mobile */}
        <AnimatePresence>
          {submitSuccess && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="mx-4 my-2 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-sm text-green-700 font-medium">
              <FiCheckCircle size={15} className="text-green-500" />
              Review submitted! It will appear after approval. Thank you 🙏
            </motion.div>
          )}
        </AnimatePresence>

        {reviewsLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
            <span className="ml-2 text-xs text-gray-500">Loading...</span>
          </div>
        )}
        {reviewsError && !reviewsLoading && (
          <div className="text-center py-4 px-4">
            <p className="text-xs text-gray-500 mb-1.5">{reviewsError}</p>
            <button onClick={fetchApprovedReviews} className="flex items-center gap-1 mx-auto text-xs text-blue-600 font-medium"><FiRefreshCw size={10} />Retry</button>
          </div>
        )}

        {!reviewsLoading && !reviewsError && reviews.length === 0 && (
          <div className="text-center py-8 px-4">
            <FiMessageSquare size={28} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-600">No reviews yet</p>
            <p className="text-xs text-gray-400 mt-0.5">Be the first to share your thoughts!</p>
          </div>
        )}

        {/* Horizontal scroll cards */}
        {!reviewsLoading && !reviewsError && reviews.length > 0 && (
          <div className="px-4 py-3">
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {visibleReviews.map((review, index) => (
                <ReviewCardMobile key={review._id || index} review={review} onPhotoClick={setLightboxImg} />
              ))}
            </div>
          </div>
        )}

        {!reviewsLoading && !reviewsError && reviews.length > 0 && (
          <button onClick={() => setShowAllReviews(v => !v)}
            className="w-full flex items-center justify-center gap-1.5 py-3 border-t border-gray-100 text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-all">
            {showAllReviews ? 'Show Less' : 'Show all reviews'}
            <FiChevronRight size={14} className={`transition-transform ${showAllReviews ? 'rotate-90' : ''}`} />
          </button>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxImg && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightboxImg(null)}>
            <motion.img initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
              src={lightboxImg} alt="review"
              className="max-w-2xl w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl"
              onClick={e => e.stopPropagation()} />
            <button onClick={() => setLightboxImg(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/40 transition">
              <FiX size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════
   FAQ SECTION
══════════════════════════════════════════════════════════════ */
const FAQ_BASE_API = `${API_BASE_URL}/faqs/product`;

const FAQSection = ({ productId }) => {
  const [faqs, setFaqs]             = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [openIndex, setOpenIndex]   = useState(null);

  useEffect(() => {
    if (!productId) return;
    (async () => {
      try {
        setFaqLoading(true);
        const res  = await fetch(`${FAQ_BASE_API}/${productId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setFaqs(data?.data || data?.faqs || (Array.isArray(data) ? data : []));
      } catch { setFaqs([]); }
      finally { setFaqLoading(false); }
    })();
  }, [productId]);

  if (faqLoading) return (
    <div className="mt-2 flex items-center gap-2 py-4">
      <div className="w-5 h-5 border-2 border-red-200 border-t-red-500 rounded-full animate-spin" />
      <span className="text-xs text-gray-400">Loading FAQs...</span>
    </div>
  );
  if (!faqs.length) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
      <div className="px-4 py-3 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <FiInfo className="text-red-500" size={15} />
          Frequently Asked Questions
          <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{faqs.length}</span>
        </h2>
      </div>
      <div className="divide-y divide-gray-100">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <div key={faq._id || index}>
              <button onClick={() => setOpenIndex(isOpen ? null : index)}
                className="w-full flex items-start justify-between gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors">
                <span className="text-sm font-semibold text-gray-800 leading-snug flex-1">{faq.question}</span>
                <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors mt-0.5 ${isOpen ? 'bg-red-100' : 'bg-gray-100'}`}>
                  {isOpen ? <FiChevronUp size={12} className="text-red-500" /> : <FiChevronDown size={12} className="text-gray-500" />}
                </div>
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                    <div className="px-4 pb-4 pt-1">
                      <div className="bg-red-50 rounded-lg p-3 border-l-2 border-red-300">
                        <p className="text-sm text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
export default function ProductDetail() {
  // const { productSlug } = useParams();
  const { categorySlug, productSlug } = useParams();
  const navigate = useNavigate();
  const { cart = [], addToCart, updateQty, removeFromCart, wishlist = [], toggleWishlistItem } = useCart();

  const [product, setProduct]                     = useState(null);
  const [productId, setProductId]                 = useState(null);
  const [vendor, setVendor]                       = useState(null);
  const [vendorBrand, setVendorBrand]             = useState(null);
  const [relatedProducts, setRelatedProducts]     = useState([]);
  const [comboProducts, setComboProducts]         = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [vendorLoading, setVendorLoading]         = useState(false);
  const [brandLoading, setBrandLoading]           = useState(false);
  const [activeImageIndex, setActiveImageIndex]   = useState(0);
  const [showImageGallery, setShowImageGallery]   = useState(false);
  const [selectedImage, setSelectedImage]         = useState(0);
  const [selectedQuantity, setSelectedQuantity]   = useState(1);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [expandedDetails, setExpandedDetails]     = useState([]);
  const [addingToCart, setAddingToCart]           = useState(false);
  const [addingCombo, setAddingCombo]             = useState(null);
  const [allProducts, setAllProducts]             = useState([]);
  const [showShipping, setShowShipping]           = useState(false);
  const [showAbout, setShowAbout]                 = useState(false);
  const [allProductImages, setAllProductImages]   = useState([]);
  const [activeMainImage, setActiveMainImage]     = useState(null);
  const [dynamicRating, setDynamicRating]         = useState(null);
  const [dynamicReviewCount, setDynamicReviewCount] = useState(null);
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState([]);

  const handleReviewStatsChange = ({ rating, reviewCount }) => {
    setDynamicRating(rating); setDynamicReviewCount(reviewCount);
  };

  const cartItem     = cart.find(item => item._id === productId);
  const cartQuantity = cartItem ? cartItem.qty : 0;
  const isWishlisted = wishlist.some(item => item._id === productId);

  const currentPrice         = product ? (selectedVariation ? selectedVariation.newPrice : product.price) : 0;
  const currentOriginalPrice = product ? (selectedVariation ? selectedVariation.oldPrice : product.originalPrice) : 0;
  const currentDiscount      = product ? (selectedVariation ? (currentOriginalPrice > currentPrice ? Math.round(((currentOriginalPrice - currentPrice) / currentOriginalPrice) * 100) : product.discount) : product.discount) : 0;
  const currentStock         = product ? (selectedVariation ? selectedVariation.stock : product.stock) : 0;

  useEffect(() => { fetchProduct(); }, [productSlug]);

  useEffect(() => {
    if (product && allProductImages.length > 0) {
      if (selectedVariation?.image && activeImageIndex >= product.images?.length) {
        setActiveMainImage(selectedVariation.image);
      } else if (allProductImages[activeImageIndex]) {
        setActiveMainImage(allProductImages[activeImageIndex]);
      }
    }
  }, [selectedVariation, activeImageIndex, product, allProductImages]);

  useEffect(() => {
    if (!product || !product.name) return;

    const item = {
      id: String(productId || product._id || product.id || productSlug),
      slug: productSlug,
      name: product.name,
      image: activeMainImage || product.image || allProductImages[0] || '',
      price: currentPrice || product.price || 0,
      originalPrice: currentOriginalPrice || product.originalPrice || 0,
      discount: currentDiscount || 0,
      stock: currentStock || product.stock || 0,
    };

    try {
      const raw = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      const filtered = raw.filter(entry => entry && entry.id !== item.id && entry.slug !== item.slug);
      const next = [item, ...filtered].slice(0, 6);
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
      setRecentlyViewedProducts(next);
    } catch (error) {
      console.error('Recently viewed save error:', error);
    }
  }, [product, productId, productSlug, activeMainImage, allProductImages, currentPrice, currentOriginalPrice, currentDiscount, currentStock]);

  const fetchAllBrands = async () => {
    try {
      setBrandLoading(true);
      const res = await fetch(PUBLIC_BRANDS_API);
      if (!res.ok) throw new Error();
      const result = await res.json();
      return result.success && result.data ? result.data : [];
    } catch { return []; }
    finally { setBrandLoading(false); }
  };

  const findBrandForRestaurant = async (targetName) => {
    const brands = await fetchAllBrands();
    if (!brands.length) return null;
    const clean = targetName.trim().toLowerCase();
    return brands.find(b => b.brandName?.trim().toLowerCase() === clean) ||
      brands.find(b => b.vendor?.name?.trim().toLowerCase() === clean) ||
      brands.find(b => b.brandName?.toLowerCase().includes(clean)) || null;
  };

  const ensureArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      if (value.includes(',')) return value.split(',').map(i => i.trim()).filter(Boolean);
      if (value && value !== 'Not specified') return [value];
      return [];
    }
    return value == null ? [] : [String(value)];
  };

  const isValidValue = (value) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '' && value.trim() !== 'Not specified';
    if (Array.isArray(value)) return value.length > 0 && !value.every(i => i === 'Not specified');
    return true;
  };

  const getDisplayValue = (value) => {
    if (!isValidValue(value)) return 'Not specified';
    if (Array.isArray(value)) return value.filter(i => i !== 'Not specified').join(', ');
    return String(value);
  };

  const extractProductsArray = (data) => {
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.products && Array.isArray(data.products)) return data.products;
    return [];
  };

  const showNotification = (message, type = 'success') => {
    window.dispatchEvent(new CustomEvent('showNotification', { detail: { message, type } }));
  };

  const fetchVendorInfo = async (productData) => {
    try {
      setVendorLoading(true);
      let vendorId = productData.vendorId || productData.vendor?._id || productData.vendor;
      if (!vendorId) {
        const map = { Havbit: '695cb686bcd6d04013c499cc', default: 'vendor-3' };
        vendorId = map[productData.restaurantName] || map.default;
      }
      const vendorData = await vendorService.getVendorById(vendorId);
      if (vendorData) { setVendor(vendorData); productData.vendorId = vendorId; productData.vendorName = vendorData.name; }
      else setVendor({ _id: vendorId, name: productData.restaurantName || 'Havbit Vendor', rating: 4.5, totalProducts: 42, totalOrders: 156, joinDate: '2024-01-15', description: 'Premium quality products', location: productData.State || 'New Delhi, India', phone: '+91 9876543210', verified: true, category: 'General Store' });
    } catch {
      setVendor({ _id: '695cb686bcd6d04013c499cc', name: product?.restaurantName || 'Havbit Vendor Store', rating: 4.5, totalProducts: 25, totalOrders: 120, joinDate: '2024-01-01', description: 'Quality products at affordable prices', location: product?.State || 'Mumbai, India', phone: '+91 9876543210', verified: true, category: 'Wellness' });
    } finally { setVendorLoading(false); }
  };

  const fetchBrandInfo = async (restaurantName) => {
    try {
      if (!restaurantName) return null;
      const targetName   = decodeURIComponent(restaurantName);
      const matchedBrand = await findBrandForRestaurant(targetName);
      if (matchedBrand) {
        setVendorBrand({ brandName: matchedBrand.brandName, logo: matchedBrand.logo || { url: matchedBrand.brandName.toLowerCase().includes('havbit') ? havbitLogo : `https://ui-avatars.com/api/?name=${encodeURIComponent(matchedBrand.brandName)}&background=linear-gradient(45deg,#f59e0b,#ef4444)&color=fff&size=256&bold=true` }, banner: matchedBrand.banner || { url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80' }, vendor: matchedBrand.vendor || { name: matchedBrand.brandName, verified: false }, stats: { rating: 4.5, productsCount: 0 } });
        return matchedBrand.brandName;
      }
      if (targetName.toLowerCase().includes('havbit')) {
        setVendorBrand({ brandName: 'Havbit', logo: { url: havbitLogo }, banner: { url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80' }, vendor: { name: 'Havbit', verified: true }, stats: { rating: 4.8, productsCount: 0 } });
        return 'Havbit';
      }
      setVendorBrand({ brandName: targetName, logo: { url: `https://ui-avatars.com/api/?name=${encodeURIComponent(targetName)}&background=linear-gradient(45deg,#f59e0b,#ef4444)&color=fff&size=256&bold=true` }, banner: { url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80' }, vendor: { name: targetName, verified: false }, stats: { rating: 4.0, productsCount: 0 } });
      return targetName;
    } catch { return null; }
  };

  const getAllImages = (p) => {
    const imgs = [];
    const pushUnique = (url) => {
      if (!url) return;
      if (!imgs.includes(url)) imgs.push(url);
    };

    pushUnique(p.image);
    if (Array.isArray(p.gallery)) p.gallery.forEach(i => pushUnique(i));
    if (p.hasVariations && Array.isArray(p.variations)) p.variations.forEach(v => pushUnique(v.image));
    if (!imgs.length) imgs.push('https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&auto=format&fit=crop&q=80');
    return imgs;
  };

  const handleThumbnailClick = (index) => {
    setActiveImageIndex(index);
    if (product?.variations) {
      const img   = allProductImages[index];
      const match = product.variations.find(v => v.image === img);
      if (match) setSelectedVariation(match);
    }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res         = await fetch(PUBLIC_PRODUCTS_API);
      if (!res.ok) throw new Error('Fetch failed');
      const data        = await res.json();
      const apiProducts = extractProductsArray(data);
      setAllProducts(apiProducts);
      let found = apiProducts.find(p =>
        getProductSlug(p) === productSlug || String(p._id || p.id) === productSlug
      );
      if (!found) {
        const isProductId = /^[a-f\d]{24}$/i.test(productSlug);
        const productEndpoint = isProductId
          ? `${API_BASE_URL}/public/product-id/${encodeURIComponent(productSlug)}`
          : `${API_BASE_URL}/public/product/${encodeURIComponent(productSlug)}`;
        const productResponse = await fetch(
          productEndpoint
        );
        if (productResponse.ok) {
          const productResult = await productResponse.json();
          found = productResult?.success ? productResult.data : null;
        }
      }
      if (found) {
        const actualId  = String(found._id || found.id);
        setProductId(actualId);
        const brandName     = found.restaurantName ? await fetchBrandInfo(found.restaurantName) : null;
        const productImages = getAllImages(found);
        setAllProductImages(productImages);
        let price = found.price || found.currentPrice || 99;
        let originalPrice = found.oldPrice || found.mrp || price * 1.5;
        let discount = Math.round(((originalPrice - price) / originalPrice) * 100) || 0;
        if (found.hasVariations && found.variations?.length) {
          const def = found.variations[0];
          price = def.newPrice || def.price || price;
          originalPrice = def.oldPrice || originalPrice;
          discount = Math.round(((originalPrice - price) / originalPrice) * 100) || 0;
        }
        const productData = {
          id: actualId, slug: getProductSlug(found), name: found.name, price, originalPrice,
          discount: discount > 0 ? discount : 0, images: productImages, image: productImages[0],
          category: found.category?.name || found.category || 'Wellness', rating: 4.2, reviewCount: 0,
          stock: found.stock || 10, savings: Math.max(originalPrice - price, 0),
          description: found.description || 'Premium quality product.', hasVariations: found.hasVariations || false,
          variations: found.variations || [], brandName: brandName || found.brandName, fssaiLicense: found.fssaiLicense,
          restaurantName: found.restaurantName, State: found.State, quality: found.quality,
          dietPreference: ensureArray(found.dietPreference), productTypes: ensureArray(found.productTypes),
          materialTypes: ensureArray(found.materialTypes), nutrition: found.nutrition, ingredients: found.ingredients,
          allergenInfo: found.allergenInfo, allergenInformation: found.allergenInformation,
          dietaryPreferences: ensureArray(found.dietaryPreferences), cuisine: found.cuisine, ageRange: found.ageRange,
          containerType: found.containerType, itemForm: found.itemForm, specialty: found.specialty,
          countryOfOrigin: found.countryOfOrigin, shelfLife: found.shelfLife, manufacturer: found.manufacturer,
          manufacturerContact: found.manufacturerContact, packerContact: found.packerContact,
          marketerNameAddress: found.marketerNameAddress, legalDisclaimer: found.legalDisclaimer,
          measurementUnit: found.measurementUnit, unitCount: found.unitCount, itemWeight: found.itemWeight,
          directions: found.directions, packageColour: found.packageColour, numberOfItems: found.numberOfItems,
          totalEaches: found.totalEaches, itemPackageWeight: found.itemPackageWeight, itemTypeName: found.itemTypeName,
          vendorId: found.vendor, vendorName: found.restaurantName,
          vendorLogo: vendorBrand?.logo?.url || (found.restaurantName?.toLowerCase().includes('havbit') ? havbitLogo : 'https://images.unsplash.com/photo-1614680376573-df3480f0c6ff?w=100&h=100&fit=crop'),
        };
        setProduct(productData);
        if (productData.hasVariations && productData.variations.length) setSelectedVariation(productData.variations[0]);
        setActiveMainImage(productData.image);
        await fetchVendorInfo(productData);
        const related = apiProducts.filter(p => String(p._id || p.id) !== actualId).slice(0, 8).map(p => {
          const rp = p.price || 99, ro = p.oldPrice || rp * 1.5;
          return { id: p._id || p.id, slug: getProductSlug(p), name: p.name, price: rp, originalPrice: ro, image: p.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400', category: p.category?.name || p.category || 'Wellness', rating: 4.0, reviewCount: 50, stock: p.stock || 10, discount: Math.round(((ro - rp) / ro) * 100) || 0, restaurantName: p.restaurantName, brandName: p.brandName, State: p.State, fssaiLicense: p.fssaiLicense };
        });
        setRelatedProducts(related.length ? related : Array.from({ length: 4 }, (_, i) => ({ id: `demo-${i}`, slug: `wellness-product-${i+1}`, name: `Wellness Product ${i+1}`, price: 299+i*100, originalPrice: 399+i*100, image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400', category: 'Wellness', rating: 4.0, reviewCount: 30, stock: 10, discount: 15, restaurantName: productData.restaurantName || 'Havbit Store' })));
        setComboProducts(createComboPacks(productData, apiProducts, related));
      } else { createDemoProduct(); }
    } catch (e) { console.error(e); createDemoProduct(); }
    finally { setLoading(false); }
  };

  const createDemoProduct = () => {
    const demoId = 'demo-product';
    setProductId(demoId);
    const demo = { id: demoId, slug: productSlug || 'premium-wellness-product', name: 'Premium Wellness Product', price: 499, originalPrice: 699, discount: 29, images: ['https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&auto=format&fit=crop&q=80'], image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&auto=format&fit=crop&q=80', category: 'Wellness', rating: 4.2, reviewCount: 0, stock: 15, savings: 200, hasVariations: true, variations: [{ variationId: 'demo-1', size: '250g', flavor: 'Original', oldPrice: 699, newPrice: 499, stock: 15, sku: 'DEMO-001', image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400' }, { variationId: 'demo-2', size: '500g', flavor: 'Chocolate', oldPrice: 1299, newPrice: 899, stock: 10, sku: 'DEMO-002', image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&2' }, { variationId: 'demo-3', size: '1kg', flavor: 'Vanilla', oldPrice: 2299, newPrice: 1699, stock: 5, sku: 'DEMO-003', image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&3' }], brandName: 'Havbit', fssaiLicense: 'FSSAI1234567890', restaurantName: 'Havbit Store', State: 'Delhi', quality: 'Premium', dietPreference: ['Veg'], productTypes: ['Supplement', 'Powder'], materialTypes: ['Food Grade Plastic'], nutrition: 'Calories: 150 kcal, Protein: 20g', ingredients: 'Natural ingredients', allergenInfo: 'Contains milk, soy', allergenInformation: 'No common allergens', dietaryPreferences: ['Vegetarian', 'No Added Sugar'], cuisine: 'Indian', description: 'Premium quality wellness product crafted with natural ingredients.', ageRange: '18+ years', containerType: 'Plastic Jar', itemForm: 'Powder', specialty: 'Organic', countryOfOrigin: 'India', shelfLife: '12 months', manufacturer: 'Havbit Foods Pvt Ltd', vendorId: '695cb686bcd6d04013c499cc', vendorName: 'Havbit Vendor', vendorLogo: havbitLogo };
    const imgs = getAllImages(demo);
    setAllProductImages(imgs);
    setProduct(demo);
    setVendorBrand({ brandName: 'Havbit', logo: { url: havbitLogo }, banner: { url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80' }, vendor: { name: 'Havbit', verified: true }, stats: { rating: 4.8, productsCount: 25 } });
    if (demo.variations?.length) setSelectedVariation(demo.variations[0]);
    setActiveMainImage(demo.image);
    const demoRelated = Array.from({ length: 8 }, (_, i) => ({ id: `demo-related-${i}`, slug: `wellness-product-${i+1}`, name: `Wellness Product ${i+1}`, price: 299+i*100, originalPrice: 399+i*100, image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400', category: 'Wellness', rating: 4.0, reviewCount: 30, stock: 10, discount: 15, restaurantName: 'Havbit Store', brandName: 'Havbit' }));
    setRelatedProducts(demoRelated);
    setComboProducts(createComboPacks(demo, [demo, ...demoRelated], demoRelated));
    setVendor({ _id: '695cb686bcd6d04013c499cc', name: 'Havbit Vendor Store', rating: 4.5, totalProducts: 25, totalOrders: 120, joinDate: '2024-01-01', description: 'Quality products at affordable prices', location: 'Mumbai, India', phone: '+91 9876543210', verified: true, category: 'Wellness' });
  };

  const createComboPacks = (main, all, related) => {
    const others = all.filter(p => String(p._id || p.id) !== String(main.id));
    return [
      { name: 'Premium Wellness Combo', badge: 'Most Popular', numProducts: 2 },
      { name: 'Ultimate Health Pack',   badge: 'Best Value',   numProducts: 2 },
      { name: 'Family Wellness Bundle', badge: 'Family Pack',  numProducts: 2 },
    ].map((cfg, idx) => {
      const pool  = [...others];
      const items = [{ id: main.id, name: main.name, image: main.image || main.images?.[0], price: main.price, position: 1 }];
      for (let i = 0; i < Math.min(cfg.numProducts - 1, pool.length); i++) {
        const ri = Math.floor(Math.random() * pool.length);
        const rp = pool.splice(ri, 1)[0];
        items.push({ id: rp._id || rp.id, name: rp.name || 'Wellness Product', image: rp.image, price: rp.price || 499, position: i + 2 });
      }
      const orig  = items.reduce((s, p) => s + (p.price || 0), 0);
      const disc  = Math.floor(Math.random() * 20) + 15;
      const final = Math.round(orig * (1 - disc / 100));
      return { id: `combo-${idx+1}`, name: cfg.name, products: items, originalPrice: orig, comboPrice: final, savings: orig - final, discount: disc, badge: cfg.badge };
    });
  };

  const handleAddToCart = async () => {
    if (!product) return;
    setAddingToCart(true);
    const item = { _id: productId, name: product.name, price: selectedVariation ? selectedVariation.newPrice : product.price, originalPrice: selectedVariation ? selectedVariation.oldPrice : product.originalPrice, image: selectedVariation?.image || product.image, category: product.category, qty: selectedQuantity, size: selectedVariation?.size || null, flavor: selectedVariation?.flavor || null, sku: selectedVariation?.sku || null, stock: selectedVariation?.stock || product.stock, variationId: selectedVariation?.variationId, vendorId: product.vendorId || vendor?._id, vendorName: product.vendorName || vendor?.name, restaurantName: product.restaurantName, fssaiLicense: product.fssaiLicense, State: product.State, brandName: product.brandName };
    await new Promise(r => setTimeout(r, 500));
    addToCart?.(item, selectedQuantity);
    const varText = selectedVariation ? ` (${selectedVariation.size || ''}${selectedVariation.flavor ? `, ${selectedVariation.flavor}` : ''})` : '';
    showNotification(`${product.name}${varText} added to cart (${selectedQuantity} items)`);
    setAddingToCart(false);
  };

  const handleAddCombo = async (combo) => {
    if (!combo?.products) return;
    setAddingCombo(combo.id);
    for (const p of combo.products) {
      if (p) { addToCart?.({ _id: p.id, name: p.name, price: p.price, image: p.image, qty: 1, combo: combo.name, restaurantName: product?.restaurantName, fssaiLicense: product?.fssaiLicense, State: product?.State, brandName: product?.brandName }, 1); }
      await new Promise(r => setTimeout(r, 300));
    }
    showNotification(`🎉 ${combo.name} added! Save ₹${combo.savings}`);
    setAddingCombo(null);
  };

  const handleToggleWishlist = () => {
    if (!product) return;
    toggleWishlistItem?.({ _id: productId, name: product.name, price: selectedVariation ? selectedVariation.newPrice : product.price, originalPrice: selectedVariation ? selectedVariation.oldPrice : product.originalPrice, image: selectedVariation?.image || product.image, category: product.category, size: selectedVariation?.size, flavor: selectedVariation?.flavor, variationId: selectedVariation?.variationId, vendorId: product.vendorId || vendor?._id, vendorName: product.vendorName || vendor?.name, restaurantName: product.restaurantName, fssaiLicense: product.fssaiLicense, State: product.State, brandName: product.brandName });
  };

  const handleViewStoreClick = () => {
    const brandName = vendorBrand?.brandName || product?.restaurantName || product?.vendorName || vendor?.name;
    if (!brandName) { showNotification('Store information not available', 'error'); return; }
    showNotification(`Loading ${brandName}'s products...`, 'info');
    navigate(`/restaurant-products/${encodeURIComponent(brandName)}`);
  };

  const handleAddRelatedToCart = (rp) => {
    if (!rp) return;
    addToCart?.({ _id: rp.id, name: rp.name, price: rp.price, originalPrice: rp.originalPrice, image: rp.image, category: rp.category, qty: 1, restaurantName: rp.restaurantName, fssaiLicense: rp.fssaiLicense, State: rp.State, brandName: rp.brandName }, 1);
    showNotification(`${rp.name} added to cart`);
  };

  const handleWishlistRelated = (rp) => {
    if (!rp) return;
    toggleWishlistItem?.({ _id: rp.id, name: rp.name, price: rp.price, originalPrice: rp.originalPrice, image: rp.image, category: rp.category, restaurantName: rp.restaurantName, fssaiLicense: rp.fssaiLicense, State: rp.State, brandName: rp.brandName });
  };

  const getProductDetails = () => {
    if (!product) return [];
    return [
      { label: 'FSSAI License',          value: product.fssaiLicense,          badge: isValidValue(product.fssaiLicense) ? 'Certified' : null, show: isValidValue(product.fssaiLicense) },
      { label: 'State/Location',          value: product.State,                 show: isValidValue(product.State) },
      { label: 'Seller',                  value: product.restaurantName,        show: isValidValue(product.restaurantName) },
      { label: 'Quality',                 value: product.quality,               show: isValidValue(product.quality) },
      { label: 'Product Type',            value: product.productTypes,          show: isValidValue(product.productTypes) },
      { label: 'Item Type Name',          value: product.itemTypeName,          show: isValidValue(product.itemTypeName) },
      { label: 'Diet Preference',         value: product.dietPreference,        show: isValidValue(product.dietPreference) },
      { label: 'Dietary Preferences',     value: product.dietaryPreferences,    show: isValidValue(product.dietaryPreferences) },
      { label: 'Nutrition Facts',         value: product.nutrition,             show: isValidValue(product.nutrition) },
      { label: 'Ingredients',             value: product.ingredients,           show: isValidValue(product.ingredients) },
      { label: 'Allergen Information',    value: product.allergenInformation,   show: isValidValue(product.allergenInformation) },
      { label: 'Allergen Info',           value: product.allergenInfo,          show: isValidValue(product.allergenInfo) },
      { label: 'Item Weight',             value: product.itemWeight,            show: isValidValue(product.itemWeight) },
      { label: 'Item Package Weight',     value: product.itemPackageWeight,     show: isValidValue(product.itemPackageWeight) },
      { label: 'Measurement Unit',        value: product.measurementUnit,       show: isValidValue(product.measurementUnit) },
      { label: 'Container Type',          value: product.containerType,         show: isValidValue(product.containerType) },
      { label: 'Item Form',               value: product.itemForm,              show: isValidValue(product.itemForm) },
      { label: 'Material Type',           value: product.materialTypes,         show: isValidValue(product.materialTypes) },
      { label: 'Package Colour',          value: product.packageColour,         show: isValidValue(product.packageColour) },
      { label: 'Manufacturer',            value: product.manufacturer,          show: isValidValue(product.manufacturer) },
      { label: 'Manufacturer Contact',    value: product.manufacturerContact,   show: isValidValue(product.manufacturerContact) },
      { label: 'Packer',                  value: product.packerContact,         show: isValidValue(product.packerContact) },
      { label: 'Marketer Name & Address', value: product.marketerNameAddress,   show: isValidValue(product.marketerNameAddress) },
      { label: 'Unit Count',              value: product.unitCount,             show: isValidValue(product.unitCount) },
      { label: 'Number of Items',         value: product.numberOfItems,         show: isValidValue(product.numberOfItems) },
      { label: 'Cuisine Type',            value: product.cuisine,               show: isValidValue(product.cuisine) },
      { label: 'Country of Origin',       value: product.countryOfOrigin,       show: isValidValue(product.countryOfOrigin) },
      { label: 'Age Range',               value: product.ageRange,              show: isValidValue(product.ageRange) },
      { label: 'Shelf Life',              value: product.shelfLife,             show: isValidValue(product.shelfLife) },
      { label: 'Specialty',               value: product.specialty,             show: isValidValue(product.specialty) },
      { label: 'Legal Disclaimer',        value: product.legalDisclaimer,       show: isValidValue(product.legalDisclaimer) },
      { label: 'Usage Directions',        value: product.directions,            show: isValidValue(product.directions) },
    ].filter(d => d.show);
  };

  const productDetails     = getProductDetails();
  const displayRating      = dynamicRating      ?? product?.rating      ?? 4.2;
  const displayReviewCount = dynamicReviewCount ?? product?.reviewCount ?? 0;

  /* ── Sub-components ── */
  const VendorInfoSection = () => {
    if (!vendor) return null;
    const displayName = vendorBrand?.brandName || product?.restaurantName || vendor?.name;
    return (
      <div className="mb-4 hidden">
        <div className="px-4 mb-4">
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-200">
                    <img src={vendorBrand?.logo?.url || product?.vendorLogo || havbitLogo} alt={displayName} className="w-full h-full object-cover" />
                  </div>
                  {vendor.verified && <div className="absolute -top-1 -right-1 bg-white rounded-full p-0.5"><FiShield className="text-blue-500" size={12} /></div>}
                </div>
                <div>
                  <button onClick={handleViewStoreClick} className="text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors text-left">{displayName}</button>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center"><FiStar className="text-red-500 fill-current" size={12} /><span className="text-xs font-semibold ml-1">{vendor.rating || 4.5}</span></div>
                    <span className="text-xs text-gray-500">•</span>
                    <span className="text-xs text-gray-600">{vendor.totalProducts || 25}+ products</span>
                  </div>
                </div>
              </div>
              <button onClick={handleViewStoreClick} className="text-xs text-blue-600 font-medium hover:text-blue-700 whitespace-nowrap">View Store</button>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="text-xs font-medium text-gray-600">Products</div><div className="text-sm font-bold text-gray-900">{vendor.totalProducts || 25}+</div></div>
              <div><div className="text-xs font-medium text-gray-600">Rating</div><div className="text-sm font-bold text-red-600">{vendor.rating || 4.5}/5</div></div>
              <div><div className="text-xs font-medium text-gray-600">Orders</div><div className="text-sm font-bold text-green-600">{vendor.totalOrders || 120}+</div></div>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-100"><div className="flex items-center gap-2"><FiMapPin className="text-gray-400" size={12} /><span className="text-xs text-gray-600">{vendor.location || 'India'}</span></div></div>
          </div>
        </div>
      </div>
    );
  };

  const VariationSelector = () => {
    if (!product.hasVariations || !product.variations?.length) return null;
    return (
      <>
        <div className="lg:hidden px-4 mb-4">
          <div className="mb-3"><h4 className="text-sm font-bold text-gray-900 mb-2">Available Options</h4><p className="text-xs text-gray-500">Select a variant to see price and stock</p></div>
          <div className="space-y-3">
            {product.variations.map(variation => {
              const isSelected = selectedVariation?.variationId === variation.variationId;
              const disc = variation.oldPrice ? Math.round(((variation.oldPrice - variation.newPrice) / variation.oldPrice) * 100) : 0;
              return (
                <button key={variation.variationId}
                  onClick={() => { setSelectedVariation(variation); const vi = allProductImages.findIndex(img => img === variation.image); if (vi !== -1) setActiveImageIndex(vi); }}
                  className={`w-full p-3 rounded-lg border transition-all ${isSelected ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <div className="flex items-center gap-2 mb-1">
                        {variation.size && <span className="text-sm font-medium text-gray-900">{variation.size}</span>}
                        {variation.flavor && <><span className="text-gray-300">|</span><span className="text-sm text-gray-700">{variation.flavor}</span></>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">₹{variation.newPrice}</span>
                        {variation.oldPrice > variation.newPrice && <><span className="text-xs text-gray-500 line-through">₹{variation.oldPrice}</span><span className="text-xs font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">-{disc}%</span></>}
                      </div>
                      {variation.stock > 0 ? <div className="text-xs text-green-600 mt-1">In Stock: {variation.stock}</div> : <div className="text-xs text-red-600 mt-1">Out of Stock</div>}
                    </div>
                    <div className={`w-5 h-5 rounded-full border ${isSelected ? 'bg-red-500 border-red-500' : 'border-gray-300'}`}>
                      {isSelected && <div className="w-full h-full flex items-center justify-center"><div className="w-2 h-2 bg-white rounded-full" /></div>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="hidden lg:block mb-5">
          <h4 className="text-sm font-bold text-gray-900 mb-3">Available Options</h4>
          <div className="grid grid-cols-3 gap-3">
            {product.variations.map(variation => {
              const isSelected = selectedVariation?.variationId === variation.variationId;
              const disc = variation.oldPrice ? Math.round(((variation.oldPrice - variation.newPrice) / variation.oldPrice) * 100) : 0;
              return (
                <button key={variation.variationId}
                  onClick={() => { setSelectedVariation(variation); const vi = allProductImages.findIndex(img => img === variation.image); if (vi !== -1) setActiveImageIndex(vi); }}
                  className={`p-3 rounded-lg border transition-all hover:shadow-md ${isSelected ? 'border-red-500 bg-red-50 ring-1 ring-red-500' : 'border-gray-200 hover:border-red-300'}`}>
                  {variation.image && <div className="w-full h-24 mb-2 rounded overflow-hidden"><img src={variation.image} alt="" className="w-full h-full object-cover" /></div>}
                  <div className="text-left">
                    <div className="flex items-center gap-2 mb-1">
                      {variation.size && <span className="text-sm font-medium text-gray-900">{variation.size}</span>}
                      {variation.flavor && <><span className="text-gray-300">|</span><span className="text-sm text-gray-700">{variation.flavor}</span></>}
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900">₹{variation.newPrice}</span>
                      {variation.oldPrice > variation.newPrice && <><span className="text-xs text-gray-500 line-through">₹{variation.oldPrice}</span>{disc > 0 && <span className="text-xs font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">-{disc}%</span>}</>}
                    </div>
                    {variation.stock > 0 ? <div className="text-xs text-green-600">In Stock: {variation.stock}</div> : <div className="text-xs text-red-600">Out of Stock</div>}
                    {variation.sku && <div className="text-xs text-gray-500 mt-1">SKU: {variation.sku}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const GalleryThumbnails = ({ vertical = false }) => {
    const [showMoreThumbs, setShowMoreThumbs] = useState(false);
    if (!allProductImages?.length) return null;

    const visibleImages = showMoreThumbs ? allProductImages : allProductImages.slice(0, 4);
    const remainingImages = allProductImages.slice(4);

    return (
      <div className={vertical ? "flex flex-col gap-3 w-[92px] shrink-0" : "grid grid-cols-4 gap-3 mt-4"}>
        {visibleImages.map((img, index) => {
          const variationForImage = product?.variations?.find(v => v.image === img);
          const actualIndex = index;
          const isActive = actualIndex === activeImageIndex;
          return (
            <button key={actualIndex} onClick={() => handleThumbnailClick(actualIndex)}
              className={`relative rounded-lg overflow-hidden border transition-all ${vertical ? 'h-[84px] w-[84px]' : 'h-24'} ${isActive ? 'border-red-500 border-2' : 'border-gray-300 hover:border-red-300'}`}>
              <img src={img} alt={`Thumbnail ${actualIndex + 1}`} className="w-full h-full object-cover" />
              {variationForImage && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] py-1 px-2 text-center">{variationForImage.size || 'Variant'}</div>}
              {isActive && <div className="absolute inset-0 border-2 border-red-500" />}
            </button>
          );
        })}

        {!showMoreThumbs && remainingImages.length > 0 && (
          <button
            type="button"
            onClick={() => setShowMoreThumbs(true)}
            className={`relative rounded-lg overflow-hidden border border-dashed border-gray-300 bg-gray-50 hover:border-red-300 hover:bg-red-50 transition-all flex items-center justify-center text-center ${vertical ? 'h-[84px] w-[84px]' : 'h-24'}`}
          >
            <div>
              <div className="text-lg font-bold text-red-500">+{remainingImages.length}</div>
              <div className="text-[10px] font-semibold text-gray-600">More</div>
            </div>
          </button>
        )}
      </div>
    );
  };

  const RecentlyViewedSection = () => {
    const visibleItems = recentlyViewedProducts.filter(item => item.id !== String(productId || product?._id || product?.id || productSlug));
    if (visibleItems.length === 0) return null;

    return (
      <div className="px-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
            <h2 className="text-sm font-semibold text-gray-900">Recently Viewed</h2>
            <button onClick={() => navigate('/products')} className="text-xs text-red-600 font-medium">View all</button>
          </div>
          <div className="p-2">
            <div className="grid grid-cols-2 gap-1.5">
              {visibleItems.slice(0, 4).map(item => {
                const safePrice = Number(item.price || 0);
                const safeOriginal = Number(item.originalPrice || 0);
                const savings = safeOriginal > safePrice ? Math.ceil(safeOriginal - safePrice) : 0;
                const itemSlug = item.slug || createSlug(item.name);

                return (
                  <div
                    key={item.id || item.slug}
                    onClick={() => navigate(`/product/${itemSlug}`)}
                    className="group bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden w-full cursor-pointer"
                  >
                    <div className="relative w-full aspect-[4/3.3] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-contain p-0.5 transition-transform duration-200 group-hover:scale-105" />
                      {item.discount > 0 && (
                        <div className="absolute top-1 left-1 bg-gradient-to-r from-red-500 to-red-400 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                          {item.discount}% OFF
                        </div>
                      )}
                      <div className="absolute top-1 right-1 bg-white/95 backdrop-blur-sm text-gray-700 px-1 py-0.5 rounded shadow-sm flex items-center gap-1 border border-gray-100">
                        <FiStar className="text-red-500 fill-current" size={9} />
                        <span className="text-[9px] font-bold">{item.rating || '4.2'}</span>
                      </div>
                    </div>

                    <div className="p-1.5">
                      <h4 className="text-[10px] font-bold text-gray-900 mb-1 line-clamp-2 h-6 hover:text-red-500">
                        {item.name}
                      </h4>

                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-[11px] font-bold text-gray-900">₹{safePrice}</span>
                        {safeOriginal > safePrice && (
                          <span className="text-[9px] text-gray-400 line-through">₹{safeOriginal}</span>
                        )}
                        {savings > 0 && (
                          <span className="text-[7px] text-green-600 font-light ml-auto">Save ₹{savings}</span>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/product/${itemSlug}`);
                        }}
                        className="w-full py-1 rounded-md font-medium text-[9px] flex items-center justify-center gap-1 bg-gradient-to-r from-red-500 to-red-400 text-white hover:shadow hover:shadow-red-200"
                      >
                        <FiShoppingCart size={8} />
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BottomSections = ({ isMobile = false }) => (
    <>
      {productDetails.length > 0 && (
        <div className="px-4 mb-4">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button onClick={() => setExpandedDetails(expandedDetails.length > 0 ? [] : productDetails.map(() => true))}
              className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white hover:bg-gray-50 transition-colors">
              <h2 className="text-sm font-semibold text-gray-900">Product {isMobile ? 'Details' : 'Specifications'} ({productDetails.length})</h2>
              {expandedDetails.length > 0 ? <FiChevronUp size={18} className="text-red-500" /> : <FiChevronDown size={18} className="text-gray-500" />}
            </button>
            <AnimatePresence>
              {expandedDetails.length > 0 && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <div className={isMobile ? "divide-y divide-gray-100" : "grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0"}>
                    {productDetails.map((detail, i) => (
                      <div key={i} className={isMobile ? "px-4 py-3 flex items-start justify-between gap-4" : "border-b border-gray-100"}>
                        {isMobile ? (
                          <><span className="text-xs font-medium text-gray-500 w-2/5">{detail.label}</span><div className="flex-1 text-right"><span className="text-xs text-gray-900 font-medium">{getDisplayValue(detail.value)}</span>{detail.badge && <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded">{detail.badge}</span>}</div></>
                        ) : (
                          <div className="flex items-start justify-between py-4 px-4"><h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">{detail.label}</h3><div className="text-right"><span className="text-sm text-gray-900 font-medium">{getDisplayValue(detail.value)}</span>{detail.badge && <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">{detail.badge}</span>}</div></div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      <div className="px-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button onClick={() => setShowShipping(!showShipping)} className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white hover:bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-900">Shipping &amp; Delivery</h2>
            {showShipping ? <FiChevronUp size={18} className="text-red-500" /> : <FiChevronDown size={18} className="text-gray-500" />}
          </button>
          <AnimatePresence>
            {showShipping && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="px-4 py-4 space-y-4">
                  {[`Ships directly from ${displayBrandName}|Estimated delivery: 3-7 business days`, 'Free shipping on orders above ₹499|No-inventory model'].map((item, i) => {
                    const [title, sub] = item.split('|');
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <svg className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                        <div><span className="text-[13px] font-bold text-gray-900 block">{title}</span><span className="text-[11px] text-gray-500">{sub}</span></div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="px-4 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button onClick={() => setShowAbout(!showAbout)} className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white hover:bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-900">About the Store</h2>
            {showAbout ? <FiChevronUp size={18} className="text-red-500" /> : <FiChevronDown size={18} className="text-gray-500" />}
          </button>
          <AnimatePresence>
            {showAbout && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="px-5 py-4">
                  <p className="text-xs text-gray-600 leading-relaxed mb-4"><span className="font-bold text-gray-900">{displayBrandName}</span> is one of India's leading food brands, committed to bringing you healthy and delicious products.</p>
                  <div className="grid grid-cols-2 gap-3 border-t border-gray-50 pt-4">
                    <div><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Type</span><span className="text-xs font-semibold text-gray-800">Verified Vendor</span></div>
                    <div><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Location</span><span className="text-xs font-semibold text-gray-800">{product.State || 'India'}</span></div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Reviews */}
      <div className="px-4 mb-4">
        <ReviewsSection productId={productId} onStatsChange={handleReviewStatsChange} />
      </div>

      <RecentlyViewedSection />

      {/* FAQ */}
      <div className="px-4 mb-4">
        <FAQSection productId={productId} />
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-3 border-red-200 border-t-red-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <div className="container mx-auto px-4 py-12 text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <button onClick={() => navigate('/products')} className="px-6 py-3 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700">Browse Products</button>
        </div>
      </div>
    );
  }

  const currentImage         = activeMainImage || selectedVariation?.image || product?.image;
  const displayBrandName     = vendorBrand?.brandName || product?.restaurantName;

  return (
    <>
      <AnimatePresence>
        {showImageGallery && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-6xl">
              <button onClick={() => setShowImageGallery(false)} className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20">
                <FiX size={24} className="text-white" />
              </button>
              <div className="relative h-[60vh] rounded-xl overflow-hidden">
                <img src={allProductImages[selectedImage]} alt={product.name} className="w-full h-full object-contain" />
                <button onClick={() => setSelectedImage(p => p === 0 ? allProductImages.length-1 : p-1)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20"><FiChevronLeft size={24} className="text-white" /></button>
                <button onClick={() => setSelectedImage(p => p === allProductImages.length-1 ? 0 : p+1)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20"><FiChevronRight size={24} className="text-white" /></button>
              </div>
              <div className="flex justify-center gap-2 mt-4">
                {allProductImages.map((_, i) => <button key={i} onClick={() => setSelectedImage(i)} className={`w-2 h-2 rounded-full ${i === selectedImage ? 'bg-red-500' : 'bg-gray-500'}`} />)}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="min-h-screen bg-white">

        {/* ══ MOBILE ══ */}
        <div className="lg:hidden">
          <div className="bg-white fixed top-0 left-0 right-0 z-30 border-b shadow-sm">
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200"><FiArrowLeft size={18} /></button>
                <div className="flex items-center gap-2">
                  <button onClick={handleToggleWishlist} className={`w-8 h-8 flex items-center justify-center rounded-lg ${isWishlisted ? 'text-red-500 bg-red-50' : 'text-gray-600 hover:text-red-500'}`}>
                    <FiHeart className={isWishlisted ? 'fill-current' : ''} size={18} />
                  </button>
                  <button onClick={() => navigate('/cart')} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:text-red-500 relative">
                    <FiShoppingCart size={18} />
                    {cart.length > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">{cart.reduce((s, i) => s + (i.qty || 1), 0)}</span>}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-0">
            <div className="px-4 pt-4">
              <div className="flex justify-between items-start gap-4">
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    {(vendorBrand?.logo?.url || product.vendorLogo) && <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200"><img src={vendorBrand?.logo?.url || product.vendorLogo || havbitLogo} alt={displayBrandName} className="w-full h-full object-cover" /></div>}
                    {isValidValue(displayBrandName) && <button onClick={handleViewStoreClick} className="text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors">{displayBrandName}</button>}
                  </div>
                  <h1 className="text-xl font-bold text-gray-900 leading-tight mb-2">{product.name}</h1>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {displayRating > 0 && <div className="flex items-center bg-red-50 px-2 py-1 rounded"><FiStar className="text-red-500 fill-current" size={12} /><span className="text-xs font-semibold ml-1">{displayRating}</span></div>}
                  <div className="flex flex-col items-end">
                    {displayReviewCount > 0 && <span className="text-[10px] text-gray-500">({displayReviewCount} ratings)</span>}
                    {currentStock > 0 ? <span className="text-[10px] text-green-600 font-medium">In Stock</span> : <span className="text-[10px] text-red-600 font-medium">Out of Stock</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 mb-4">
              <div className="relative aspect-square bg-gray-50 rounded-xl overflow-hidden">
                <div className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                  onScroll={e => { const w = e.currentTarget.offsetWidth; const ni = Math.round(e.currentTarget.scrollLeft / w); if (ni !== activeImageIndex) setActiveImageIndex(ni); }}>
                  {allProductImages.map((img, index) => (
                    <div key={index} className="w-full h-full flex-shrink-0 snap-center">
                      <img src={img} alt={`${product.name} ${index+1}`} className="w-full h-full object-contain p-4" onClick={() => { setSelectedImage(index); setShowImageGallery(true); }} />
                    </div>
                  ))}
                </div>
                {currentDiscount > 0 && <div className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-md shadow-sm pointer-events-none">-{currentDiscount}%</div>}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 pointer-events-none">
                  {allProductImages.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === activeImageIndex ? 'bg-red-500' : 'bg-gray-300'}`} />)}
                </div>
              </div>
              <div className="flex overflow-x-auto gap-2 mt-3 pb-2">
                {allProductImages.map((img, index) => (
                  <button key={index} onClick={() => handleThumbnailClick(index)} className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border ${index === activeImageIndex ? 'border-red-500 border-2' : 'border-gray-300'}`}>
                    <img src={img} alt={`Thumbnail ${index+1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {isValidValue(product.State) && (
              <div className="px-4 mb-4 flex items-center justify-between">
                <a href={`/city/${product.State}`} className="flex items-center gap-2.5 group no-underline">
                  <div className="p-1.5 bg-blue-50 rounded-lg group-hover:bg-blue-600 transition-colors"><FiMapPin className="text-blue-600 group-hover:text-white" size={16} /></div>
                  <span className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{product.State}, India</span>
                </a>
                <ShareButton product={product} />
              </div>
            )}

            <VariationSelector />

            <div className="px-4 mb-4">
              <div className="bg-red-50 rounded-xl p-4">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xl font-bold text-gray-900">₹{currentPrice}</span>
                  {currentOriginalPrice > currentPrice && <><span className="text-lg text-gray-500 line-through">₹{currentOriginalPrice}</span><span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded">Save {currentDiscount}%</span></>}
                </div>
                {currentDiscount > 0 && <p className="text-sm text-red-600 font-medium">You save: ₹{(currentOriginalPrice - currentPrice).toFixed(1)}</p>}
              </div>
            </div>

            <div className="px-4 mb-4">
              {cartQuantity > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-2 border-red-200 bg-red-50 rounded-lg p-3">
                    <button onClick={() => { if (cartQuantity > 1) updateQty?.(productId, cartQuantity-1); else removeFromCart?.(productId); }} className="w-10 h-10 flex items-center justify-center rounded-lg bg-white hover:bg-red-100 text-red-600 shadow-sm"><FiMinus size={18} /></button>
                    <span className="text-lg font-bold text-red-700">{cartQuantity}</span>
                    <button onClick={() => updateQty?.(productId, cartQuantity+1)} disabled={currentStock <= cartQuantity} className={`w-10 h-10 flex items-center justify-center rounded-lg bg-white hover:bg-red-100 text-red-600 shadow-sm ${currentStock <= cartQuantity ? 'opacity-50 cursor-not-allowed' : ''}`}><FiPlus size={18} /></button>
                  </div>
                  <button onClick={() => navigate('/cart')} className="w-full py-3.5 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 shadow-lg">View Cart</button>
                </div>
              ) : (
                <button onClick={handleAddToCart} disabled={currentStock === 0 || addingToCart || (product.hasVariations && !selectedVariation)}
                  className={`w-full py-3.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 ${currentStock === 0 || addingToCart || (product.hasVariations && !selectedVariation) ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700 shadow-lg'}`}>
                  {addingToCart ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Adding...</>) : currentStock === 0 ? 'Out of Stock' : product.hasVariations && !selectedVariation ? 'Select an Option' : (<><FiShoppingCart size={18} />Add to Cart</>)}
                </button>
              )}
            </div>

            <ProductBadges />

            {isValidValue(product.description) && (
              <div className="px-4 mb-4">
                <h3 className="text-sm font-bold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{product.description}</p>
              </div>
            )}

            <VendorInfoSection />

            {/* Combo Mobile */}
            <div className="px-4 mb-6 mt-2">
              <div className="mb-3"><h2 className="text-base font-bold text-gray-900">Frequently bought together</h2><p className="text-xs text-gray-500 mt-1">Customers usually buy these items together</p></div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-center gap-4 mb-4">
                  {comboProducts.slice(0, 1).map(combo => (
                    <React.Fragment key={combo.id}>
                      {combo.products?.slice(0, 2).map((p, idx) => (
                        <React.Fragment key={p?.id || idx}>
                          <div className="flex items-center">
                            <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden border"><img src={p.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'} alt={p.name} className="w-full h-full object-cover" /></div>
                            {idx === 0 && <div className="ml-2 mr-2"><div className="w-5 h-5 bg-gray-300 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold">+</div></div>}
                          </div>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
                <div className="border-t border-gray-100 pt-4">
                  <div className="mb-3"><p className="text-xs text-gray-600 mb-1">Total price:</p><div className="flex items-baseline gap-2"><span className="text-lg font-bold text-gray-900">₹{comboProducts[0]?.comboPrice || '1,023'}</span><span className="text-xs text-green-600 font-medium">Save ₹{Math.ceil(comboProducts[0]?.savings || 200)}</span></div></div>
                  <button onClick={() => comboProducts[0] && handleAddCombo(comboProducts[0])} disabled={addingCombo === comboProducts[0]?.id}
                    className="w-full py-2.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                    {addingCombo === comboProducts[0]?.id ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Adding...</>) : (<><FiShoppingCart size={12} />Add both to Cart</>)}
                  </button>
                </div>
              </div>
            </div>

            {/* Similar Products Mobile */}
            <div className="mb-6 px-4">
              <div className="mb-3 flex justify-between items-center">
                <h2 className="text-base font-bold text-gray-900">Similar Products</h2>
                <button onClick={() => navigate('/products')} className="text-xs text-red-600 font-medium">View All</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {relatedProducts.slice(0, 4).map(rp => {
                  const cqty  = cart.find(i => i._id === rp.id)?.qty || 0;
                  const isWish = wishlist.some(i => i._id === rp.id);
                  return (
                    <div key={rp.id} className="group relative cursor-pointer flex flex-col">
                      <div className="relative aspect-square overflow-hidden rounded-lg mb-2 bg-gray-50">
                        <img src={rp.image} alt={rp.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" onClick={() => navigate(`/product/${rp.slug || createSlug(rp.name)}`)} />
                        {rp.discount > 0 && <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-medium rounded">-{rp.discount}%</div>}
                        <div className="absolute top-1 right-1"><button onClick={e => { e.stopPropagation(); handleWishlistRelated(rp); }} className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-red-50"><FiHeart size={12} className={isWish ? 'text-red-500 fill-current' : 'text-gray-500'} /></button></div>
                      </div>
                      <div className="flex flex-col flex-grow px-0.5">
                        <h3 className="text-xs font-medium text-gray-900 mb-1 line-clamp-2 hover:text-red-600 cursor-pointer" onClick={() => navigate(`/product/${rp.slug || createSlug(rp.name)}`)}>{rp.name}</h3>
                        <div className="flex items-baseline gap-1 mb-2"><span className="text-sm font-semibold text-gray-900">₹{rp.price}</span>{rp.originalPrice > rp.price && <span className="text-xs text-gray-400 line-through">₹{rp.originalPrice}</span>}</div>
                        {isValidValue(rp.fssaiLicense) && <div className="mb-2"><div className="inline-flex items-center gap-1 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded text-[10px]"><FiShield className="text-green-600" size={8} /><span className="text-green-700 font-bold">FSSAI</span></div></div>}
                        <div className="mt-auto">
                          {cqty > 0 ? (
                            <div className="flex items-center justify-between border border-red-200 bg-red-50 rounded-lg p-1">
                              <button onClick={e => { e.stopPropagation(); if (cqty > 1) updateQty?.(rp.id, cqty-1); else removeFromCart?.(rp.id); }} className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-100 text-red-600"><FiMinus size={10} /></button>
                              <span className="text-xs font-medium text-red-600">{cqty}</span>
                              <button onClick={e => { e.stopPropagation(); updateQty?.(rp.id, cqty+1); }} className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-100 text-red-600"><FiPlus size={10} /></button>
                            </div>
                          ) : (
                            <button onClick={e => { e.stopPropagation(); handleAddRelatedToCart(rp); }} disabled={rp.stock === 0}
                              className={`w-full py-1.5 rounded text-xs font-medium flex items-center justify-center gap-1 ${rp.stock === 0 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-red-500 text-white hover:bg-red-600'}`}>
                              {rp.stock === 0 ? 'Out of Stock' : (<><FiShoppingCart size={10} />Add to Cart</>)}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <BottomSections isMobile={true} />
          </div>
        </div>

        {/* ══ DESKTOP ══ */}
        <div className="hidden lg:block">
          <div className="container mx-auto px-6 py-6">
            {/* <div className="flex items-center gap-2 mb-6 flex-wrap">
              <button onClick={() => navigate('/')} className="text-sm text-gray-600 hover:text-red-600"><FiHome size={16} /></button>
              <FiChevronRight size={14} className="text-gray-400" />
              <button onClick={() => navigate('/products')} className="text-sm text-gray-600 hover:text-red-600">Products</button>
              <FiChevronRight size={14} className="text-gray-400" />
              <span className="text-sm text-gray-900 font-medium line-clamp-1 max-w-sm">{product.name}</span>
            </div> */}

  <div className="flex items-center gap-2 mb-6 flex-wrap">
  <button
    onClick={() => navigate('/')}
    className="text-sm text-gray-600 hover:text-red-600"
  >
    <FiHome size={16} />
  </button>

  <FiChevronRight size={14} className="text-gray-400" />

  <button
    onClick={() => navigate(`/${categorySlug}`)}
    className="text-sm text-gray-600 hover:text-red-600 capitalize"
  >
    {categorySlug?.replace(/-/g, ' ')}
  </button>

  <FiChevronRight size={14} className="text-gray-400" />

  <span className="text-sm text-gray-900 font-medium line-clamp-1 max-w-sm">
    {product?.name}
  </span>
</div>

            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-5">
                <div className="sticky top-24">
                  <div className="flex items-start gap-4">
                    <GalleryThumbnails vertical />
                    <div className="relative flex-1 h-[500px] bg-gray-50 rounded-lg overflow-hidden">
                      <div className="w-full h-full transition-transform duration-500 ease-out hover:scale-[2] cursor-zoom-in"
                        style={{ backgroundImage: `url(${currentImage})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', transformOrigin: 'center center' }}
                        onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.transformOrigin = `${((e.clientX-r.left)/r.width)*100}% ${((e.clientY-r.top)/r.height)*100}%`; }}
                        onClick={() => { setSelectedImage(activeImageIndex); setShowImageGallery(true); }} />
                      {currentDiscount > 0 && <div className="absolute top-6 left-6 bg-red-500 text-white text-sm font-bold px-4 py-2 rounded-md shadow-lg z-10">-{currentDiscount}% OFF</div>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-4">
                <div className="mb-5">
                  <div className="px-4 pt-4">
                    <div className="flex items-center justify-between gap-3 mb-4 border-b border-gray-50 pb-3">
                      <div className="flex items-center gap-3">
                        {(vendorBrand?.logo?.url || product.vendorLogo) && <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-100 shadow-sm shrink-0"><img src={vendorBrand?.logo?.url || product.vendorLogo || havbitLogo} alt={displayBrandName} className="w-full h-full object-cover" /></div>}
                        {isValidValue(displayBrandName) && <div className="flex flex-col"><button onClick={handleViewStoreClick} className="text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors text-left leading-tight">{displayBrandName}</button><p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Verified Store</p></div>}
                      </div>
                      {displayRating > 0 && (
                        <div className="flex flex-col items-end shrink-0">
                          <div className="flex items-center bg-gray-50 px-2 py-1 rounded-md border border-gray-100"><FiStar className="text-red-500 fill-red-500" size={12} /><span className="text-xs font-black ml-1 text-gray-800">{displayRating}</span></div>
                          {displayReviewCount > 0 && <span className="text-[9px] text-gray-400 font-bold mt-1 uppercase">{displayReviewCount} reviews</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <h1 className="text-lg font-bold text-gray-900 mb-2 leading-tight">{product.name}</h1>
                </div>
                <VariationSelector />
                <div className="mb-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl font-bold text-gray-900">₹{currentPrice}</span>
                      {currentOriginalPrice > currentPrice && <><span className="text-sm text-gray-500 line-through">₹{currentOriginalPrice}</span><span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">Save {currentDiscount}%</span></>}
                    </div>
                    <ShareButton product={product} />
                  </div>
                  {currentDiscount > 0 && <p className="text-sm text-red-600 font-medium mt-2">You save: ₹{(currentOriginalPrice - currentPrice).toFixed(1)} ({currentDiscount}% off)</p>}
                </div>
                {isValidValue(product.State) && <div className="mb-5"><div className="flex items-center gap-2"><FiMapPin className="text-blue-600" size={14} /><a href={`/city/${product.State}`} className="text-sm font-bold text-gray-900 hover:text-blue-600 hover:underline">{product.State}, India</a></div></div>}
                {isValidValue(product.description) && <div className="mb-5"><h3 className="text-sm font-bold text-gray-900 mb-2">Description</h3><p className="text-gray-700 text-sm leading-relaxed">{product.description}</p></div>}
              </div>

              <div className="col-span-3">
                <div className="sticky top-24 border border-gray-200 rounded-xl p-4 shadow-md">
                  <div className="mb-4">
                    <span className="text-xl font-bold text-gray-900">₹{currentPrice}</span>
                    {currentOriginalPrice > currentPrice && <div className="flex items-center gap-2 mt-1"><span className="text-sm text-gray-500 line-through">₹{currentOriginalPrice}</span><span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">Save {currentDiscount}%</span></div>}
                  </div>
                  <div className="mb-5">
                    {cartQuantity > 0 ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between border border-red-200 bg-red-50 rounded-lg p-2">
                          <button onClick={() => updateQty?.(productId, cartQuantity-1)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white hover:bg-red-100 text-red-600 shadow-sm"><FiMinus size={14} /></button>
                          <span className="text-lg font-bold text-red-700">{cartQuantity}</span>
                          <button onClick={() => updateQty?.(productId, cartQuantity+1)} disabled={currentStock <= cartQuantity} className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white hover:bg-red-100 text-red-600 shadow-sm ${currentStock <= cartQuantity ? 'opacity-50 cursor-not-allowed' : ''}`}><FiPlus size={14} /></button>
                        </div>
                        <button onClick={() => navigate('/cart')} className="w-full py-2.5 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 shadow-sm">View Cart</button>
                      </div>
                    ) : (
                      <button onClick={handleAddToCart} disabled={currentStock === 0 || addingToCart || (product.hasVariations && !selectedVariation)}
                        className={`w-full py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-sm ${currentStock === 0 || addingToCart || (product.hasVariations && !selectedVariation) ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                        {addingToCart ? (<><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Adding...</>) : currentStock === 0 ? 'Out of Stock' : product.hasVariations && !selectedVariation ? 'Select an Option' : (<><FiShoppingCart size={12} />Add to Cart</>)}
                      </button>
                    )}
                  </div>
                  <ProductBadges />
                </div>
              </div>
            </div>

            {/* Combo Desktop */}
            <div className="mt-8 mb-8">
              <div className="mb-4"><h2 className="text-xl font-bold text-gray-900">Frequently bought together</h2><p className="text-sm text-gray-500 mt-1">Customers usually buy these items together</p></div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col md:flex-row md:items-start gap-8 shadow-sm">
                <div className="flex items-center gap-4 md:gap-8 flex-shrink-0">
                  {comboProducts.slice(0, 1).map(combo => (
                    <React.Fragment key={combo.id}>
                      {combo.products?.slice(0, 2).map((p, idx) => (
                        <React.Fragment key={p?.id || idx}>
                          <div className="flex items-center">
                            <div className="w-20 h-20 md:w-32 md:h-32 bg-gray-50 rounded-lg overflow-hidden border border-gray-100 p-2 hover:shadow-md transition-shadow"><img src={p.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'} alt={p.name} className="w-full h-full object-contain" /></div>
                            {idx === 0 && <div className="mx-3 md:mx-6"><div className="w-6 h-6 md:w-8 md:h-8 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center text-lg font-light">+</div></div>}
                          </div>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex-grow">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50/50 p-4 rounded-lg border border-dashed border-gray-200">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Total Price for both:</p>
                      <div className="flex items-center gap-3"><span className="text-2xl font-black text-gray-900">₹{comboProducts[0]?.comboPrice || '1,023'}</span><span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">Save ₹{comboProducts[0]?.savings || '200'}</span></div>
                    </div>
                    <button onClick={() => comboProducts[0] && handleAddCombo(comboProducts[0])} disabled={addingCombo === comboProducts[0]?.id}
                      className="w-full md:w-auto px-8 py-3 bg-red-500 hover:bg-red-600 text-white text-sm font-bold rounded-full transition-all shadow-lg flex items-center justify-center gap-2">
                      {addingCombo === comboProducts[0]?.id ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (<><FiShoppingCart size={16} />Add both to Cart</>)}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Similar Products Desktop */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Similar Products</h2>
              <div className="grid grid-cols-6 gap-6">
                {relatedProducts.slice(0, 6).map(rp => {
                  const cqty  = cart.find(i => i._id === rp.id)?.qty || 0;
                  const isWish = wishlist.some(i => i._id === rp.id);
                  return (
                    <div key={rp.id} className="group relative cursor-pointer flex flex-col" onClick={() => navigate(`/product/${rp.slug || createSlug(rp.name)}`)}>
                      <div className="relative aspect-square overflow-hidden rounded-xl mb-4 bg-gray-50">
                        <img src={rp.image} alt={rp.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                        {rp.discount > 0 && <div className="absolute top-3 left-3 px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded shadow-lg">-{rp.discount}%</div>}
                        <div className="absolute bottom-3 right-3"><button onClick={e => { e.stopPropagation(); handleWishlistRelated(rp); }} className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-50"><FiHeart size={16} className={isWish ? 'text-red-500 fill-current' : 'text-gray-500'} /></button></div>
                      </div>
                      <div className="flex flex-col flex-grow px-1">
                        <h3 className="text-sm font-medium text-gray-900 mb-3 line-clamp-2 hover:text-red-600">{rp.name}</h3>
                        <div className="mb-4"><div className="flex items-baseline gap-2"><span className="text-lg font-semibold text-gray-900">₹{rp.price}</span>{rp.originalPrice > rp.price && <span className="text-sm text-gray-400 line-through">₹{rp.originalPrice}</span>}</div></div>
                        <div className="mt-auto">
                          {cqty > 0 ? (
                            <div className="flex items-center justify-between border border-red-200 bg-red-50 rounded-xl p-3">
                              <button onClick={e => { e.stopPropagation(); if (cqty > 1) updateQty?.(rp.id, cqty-1); else removeFromCart?.(rp.id); }} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-100 text-red-600"><FiMinus size={14} /></button>
                              <span className="text-base font-medium text-red-600">{cqty}</span>
                              <button onClick={e => { e.stopPropagation(); updateQty?.(rp.id, cqty+1); }} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-100 text-red-600"><FiPlus size={14} /></button>
                            </div>
                          ) : (
                            <button onClick={e => { e.stopPropagation(); handleAddRelatedToCart(rp); }} disabled={rp.stock === 0}
                              className={`w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all ${rp.stock === 0 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-red-500 text-white hover:bg-red-600 shadow-md hover:shadow-lg'}`}>
                              {rp.stock === 0 ? 'Out of Stock' : (<><FiShoppingCart size={14} />Add to Cart</>)}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <BottomSections isMobile={false} />
          </div>
        </div>
      </div>
    </>
  );
}

export const markOrderConfirmed = (orderId, productIds) => {
  try {
    const existing = JSON.parse(localStorage.getItem('confirmed_orders') || '[]');
    if (!existing.some(o => o.orderId === String(orderId))) {
      existing.push({ orderId: String(orderId), productIds: productIds.map(String), confirmedAt: new Date().toISOString() });
      localStorage.setItem('confirmed_orders', JSON.stringify(existing));
    }
  } catch (e) { console.error('markOrderConfirmed error:', e); }
};























